import { JOB_ISSUES_ENDPOINT } from "../../../../consts";
import { useCRUD } from "../../../../api/crud";
import { useParams } from "react-router-dom";
import { ReactNode, useEffect, useState } from "react";
import CustomCircularProgress from "../../../../components/ui/customCircularProgress/customCircularProgress.component";
import { Box, Typography, Divider, Chip } from "@mui/material";
import { basicInfoContainerStyle } from "../../../../styles/commonStyles";
import { useTranslation } from "react-i18next";
import { useHasPermission } from "../../../../contexts/auth";
import { formatDateTime } from "../../../../utils";
import { useBreadcrumbs } from "../../../../components/ui/breadcrumps/breadcrumbsContext";

type DetailRowProps = {
  label: string;
  children: ReactNode;
  noBorder?: boolean;
};

const DetailRow = ({ label, children, noBorder = false }: DetailRowProps) => (
  <Box
    sx={{
      display: "grid",
      gridTemplateColumns: { xs: "1fr", sm: "200px 1fr" },
      gap: { xs: 0.5, sm: 2 },
      alignItems: "start",
      py: 1.25,
      px: 1.5,
      borderBottom: noBorder ? "none" : "1px solid",
      borderColor: "divider",
    }}
  >
    <Typography variant="body2" sx={{ color: "text.secondary", fontWeight: 600 }}>
      {label}
    </Typography>
    <Box sx={{ minHeight: "24px", display: "flex", alignItems: "center" }}>{children}</Box>
  </Box>
);

export const JobIssueBasicInfo = () => {
  const { id } = useParams();
  const { t } = useTranslation();
  const { get } = useCRUD(JOB_ISSUES_ENDPOINT);
  const { setOverride } = useBreadcrumbs();
  const [jobIssue, setJobIssue] = useState<any | null>(null);
  const canViewJobIssues = useHasPermission("service.view_jobissue");

  useEffect(() => {
    const fetchJobIssue = async () => {
      if (id) {
        const response = await get(id);
        if (response.data) {
          setJobIssue(response.data);
          const breadcrumbLabel = response.data.code
            ? `${response.data.title} (${response.data.code})`
            : response.data.title || id;
          setOverride(`/service/issues-catalog/${id}`, breadcrumbLabel);
        }
      }
    };

    const handleJobIssueUpdated = () => {
      fetchJobIssue();
    };

    window.addEventListener("jobIssueUpdated", handleJobIssueUpdated);
    fetchJobIssue();
    return () => {
      window.removeEventListener("jobIssueUpdated", handleJobIssueUpdated);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  if (!canViewJobIssues) {
    return null;
  }

  if (!jobIssue) return <CustomCircularProgress />;

  return (
    <Box sx={basicInfoContainerStyle}>
      <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
        <Typography variant="h5" component="h1" sx={{ fontWeight: 600 }}>
          {jobIssue.title}
        </Typography>
      </Box>

      <Divider />

      <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
        <Typography variant="h6" sx={{ fontWeight: 600, mb: 1 }}>
          {t("Basic Information")}
        </Typography>
        <Box
          sx={{
            border: "1px solid",
            borderColor: "divider",
            borderRadius: 1,
            overflow: "hidden",
          }}
        >
          <DetailRow label={t("Title")}>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1, flexWrap: "wrap" }}>
              {jobIssue.title && (
                <Chip
                  label={jobIssue.title}
                  size="small"
                  sx={
                    jobIssue.color
                      ? {
                          bgcolor: jobIssue.color,
                          color: "white",
                          "& .MuiChip-label": { color: "white" },
                        }
                      : {
                          bgcolor: "rgba(128, 128, 128, 0.25)",
                          color: "text.secondary",
                        }
                  }
                />
              )}
              {!jobIssue.title && (
                <Typography variant="subtitle1">-</Typography>
              )}
            </Box>
          </DetailRow>
          {jobIssue.code && (
            <DetailRow label={t("Code")}>
              <Typography variant="body2" sx={{ fontFamily: "monospace" }}>
                {jobIssue.code}
              </Typography>
            </DetailRow>
          )}
          {jobIssue.description && (
            <DetailRow label={t("Description")} noBorder>
              <Typography
                variant="body2"
                sx={{
                  whiteSpace: "pre-wrap",
                  color: "text.secondary",
                }}
              >
                {jobIssue.description}
              </Typography>
            </DetailRow>
          )}
        </Box>
      </Box>

      <Divider />

      <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
        <Typography variant="h6" sx={{ fontWeight: 600, mb: 1 }}>
          {t("Metadata")}
        </Typography>
        <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
          <Typography variant="body2" color="text.secondary">
            <strong>{t("Created At")}:</strong> {formatDateTime(jobIssue.created_at)}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            <strong>{t("Updated At")}:</strong> {formatDateTime(jobIssue.updated_at)}
          </Typography>
        </Box>
      </Box>
    </Box>
  );
};
